import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Gift, Check, ArrowUpRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";

interface WelcomeCreditsCardProps {
  creditsRemaining: number;
  creditsMonthly: number;
}

export function WelcomeCreditsCard({ creditsRemaining, creditsMonthly }: WelcomeCreditsCardProps) {
  const progressPercent = Math.min((creditsRemaining / creditsMonthly) * 100, 100);

  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/10 via-card to-card p-6 sm:p-7 shadow-fluent-2">
      {/* decorative blur */}
      <div className="absolute -top-20 -right-20 h-56 w-56 rounded-full bg-primary/15 blur-3xl pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-primary to-primary-glow text-primary-foreground flex items-center justify-center shadow-fluent-2">
            <Gift className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-semibold">Welcome credits</h3>
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">
                <Sparkles className="h-2.5 w-2.5" />
                Free
              </span>
            </div>
            <p className="text-sm text-muted-foreground">Your free credits to get started</p>
          </div>
        </div>

        {/* Balance */}
        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Credits remaining</span>
            <div className="flex items-baseline gap-1.5">
              <span className="text-4xl font-bold tabular-nums tracking-tight">{creditsRemaining}</span>
              <span className="text-xs text-muted-foreground">/ {creditsMonthly}</span>
            </div>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>

        {/* Perks */}
        <div className="my-5 grid grid-cols-2 gap-3 py-4 border-y border-border/40">
          <div className="flex items-center gap-2 text-sm">
            <div className="h-5 w-5 rounded-full bg-success/10 flex items-center justify-center shrink-0">
              <Check className="h-3 w-3 text-success" />
            </div>
            <span className="text-muted-foreground">Never expire</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <div className="h-5 w-5 rounded-full bg-success/10 flex items-center justify-center shrink-0">
              <Check className="h-3 w-3 text-success" />
            </div>
            <span className="text-muted-foreground">Use for any feature</span>
          </div>
        </div>

        {/* CTA */}
        <Button asChild className="w-full shadow-fluent-2" size="lg">
          <Link to="/plans" className="gap-2">
            Subscribe for more credits
            <ArrowUpRight className="h-4 w-4" />
          </Link>
        </Button>
        <p className="text-xs text-center text-muted-foreground mt-3">
          Unlock Smart Categories, Smart Notes, monthly renewals, and more.
        </p>
      </div>
    </div>
  );
}
